import React, { useEffect } from 'react'
import styled from 'styled-components'
import { useNavigate } from 'react-router-dom'

const Container = styled.div`
  width: 100%;
  min-height: 100vh;
  background-color: #ffffff;
  font-family: -apple-system, system-ui, BlinkMacSystemFont, 'Segoe UI', Helvetica, Arial, sans-serif;
  font-size: 18px;
  line-height: 1.8;
  color: #2d3748;
`

const HeroSection = styled.section`
  padding-top: 100px;
  padding-bottom: 60px;
  background: linear-gradient(135deg, #ec4899 0%, #db2777 100%);
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(135deg, rgba(236, 72, 153, 0.9) 0%, rgba(219, 39, 119, 0.9) 100%);
    z-index: 0;
  }

  @media (max-width: 800px) {
    padding-top: 80px;
    padding-bottom: 40px;
  }
`

const MaxWidthContainer = styled.div`
  max-width: 1300px;
  margin: 0 auto;
  padding: 0 30px;
  position: relative;
  z-index: 1;

  @media (max-width: 800px) {
    padding: 0 20px;
  }
`

const HeroContent = styled.div`
  text-align: center;
  max-width: 900px;
  margin: 0 auto;
`

const ProductLogo = styled.h1`
  font-size: 56px;
  font-weight: 700;
  color: #ffffff;
  margin-bottom: 20px;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);

  @media (max-width: 800px) {
    font-size: 40px;
  }
`

const HeroTagline = styled.p`
  font-size: 24px;
  font-weight: 300;
  color: rgba(255, 255, 255, 0.95);
  margin-bottom: 30px;

  @media (max-width: 800px) {
    font-size: 20px;
  }
`

const HeroDescription = styled.p`
  font-size: 18px;
  line-height: 1.8;
  color: rgba(255, 255, 255, 0.9);
  margin-bottom: 40px;
`

const CTAButton = styled.button`
  background: #ffffff;
  color: #ec4899;
  font-size: 18px;
  font-weight: 700;
  padding: 18px 50px;
  border: none;
  border-radius: 9999px;
  cursor: pointer;
  box-shadow: 0 12px 30px rgba(0, 0, 0, 0.3);
  transition: all 500ms ease-in-out;

  &:hover {
    transform: translate3d(0, -4px, 0);
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.4);
    background: #FFD93D;
    color: #2d3748;
  }

  @media (max-width: 800px) {
    width: 100%;
    padding: 18px 40px;
  }
`

const Section = styled.section`
  padding: 100px 0;

  @media (max-width: 800px) {
    padding: 60px 0;
  }
`

const OverviewSection = styled(Section)`
  background: linear-gradient(180deg, #f7fafc 0%, #edf2f7 100%);
`

const SectionTitle = styled.h2`
  font-size: 36px;
  font-weight: 700;
  line-height: 1.3;
  color: #1a202c;
  text-align: center;
  margin-bottom: 20px;

  @media (max-width: 800px) {
    font-size: 28px;
  }
`

const SectionSubtitle = styled.p`
  font-size: 18px;
  line-height: 1.8;
  color: #4a5568;
  margin-bottom: 30px;
  text-align: center;
  max-width: 800px;
  margin-left: auto;
  margin-right: auto;
`

const FeaturesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 40px;
  margin-top: 60px;

  @media (max-width: 800px) {
    grid-template-columns: 1fr;
    gap: 30px;
  }
`

const FeatureCard = styled.div`
  background: white;
  padding: 40px;
  border-radius: 16px;
  box-shadow: 0px 4px 20px rgba(236, 72, 153, 0.1);
  transition: all 500ms ease-in-out;
  text-align: center;

  &:hover {
    transform: translate3d(0, -10px, 0);
    box-shadow: 0px 12px 40px rgba(236, 72, 153, 0.2);
  }
`

const FeatureIcon = styled.div`
  width: 80px;
  height: 80px;
  background: linear-gradient(135deg, #ec4899 0%, #8b5cf6 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 40px;
  margin: 0 auto 25px;
`

const FeatureTitle = styled.h3`
  font-size: 22px;
  font-weight: 700;
  color: #1a202c;
  margin-bottom: 15px;
`

const FeatureDescription = styled.p`
  font-size: 16px;
  line-height: 1.6;
  color: #4a5568;
`

const BenefitsSection = styled(Section)`
  background: linear-gradient(180deg, #ffffff 0%, #f7fafc 100%);
`

const BenefitsList = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 30px;
  margin-top: 60px;

  @media (max-width: 800px) {
    grid-template-columns: 1fr;
  }
`

const BenefitItem = styled.div`
  display: flex;
  gap: 20px;
  align-items: flex-start;
`

const BenefitIcon = styled.div`
  font-size: 32px;
  color: #ec4899;
  flex-shrink: 0;
`

const BenefitContent = styled.div``

const BenefitTitle = styled.h4`
  font-size: 20px;
  font-weight: 700;
  color: #1a202c;
  margin-bottom: 10px;
`

const BenefitText = styled.p`
  font-size: 16px;
  line-height: 1.6;
  color: #4a5568;
`

const HowItWorksSection = styled(Section)`
  background: linear-gradient(180deg, #edf2f7 0%, #e2e8f0 100%);
`

const StepsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 30px;
  margin-top: 60px;

  @media (max-width: 800px) {
    grid-template-columns: 1fr;
  }
`

const StepCard = styled.div`
  background: white;
  padding: 30px;
  border-radius: 12px;
  text-align: center;
  position: relative;
  box-shadow: 0px 4px 20px rgba(236, 72, 153, 0.1);
`

const StepNumber = styled.div`
  width: 50px;
  height: 50px;
  background: linear-gradient(135deg, #ec4899 0%, #8b5cf6 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  font-weight: 700;
  color: white;
  margin: 0 auto 20px;
`

const StepTitle = styled.h4`
  font-size: 18px;
  font-weight: 700;
  color: #1a202c;
  margin-bottom: 10px;
`

const StepDescription = styled.p`
  font-size: 15px;
  line-height: 1.6;
  color: #4a5568;
`

const CTASection = styled(Section)`
  background: linear-gradient(135deg, #ec4899 0%, #8b5cf6 100%);
  color: white;
  text-align: center;
`

const CTATitle = styled.h2`
  font-size: 36px;
  font-weight: 700;
  margin-bottom: 20px;
  color: white;

  @media (max-width: 800px) {
    font-size: 28px;
  }
`

const CTADescription = styled.p`
  font-size: 18px;
  margin-bottom: 40px;
  color: rgba(255, 255, 255, 0.95);
  max-width: 700px;
  margin-left: auto;
  margin-right: auto;
`

const CTAButtonWhite = styled(CTAButton)`
  background: white;
  color: #ec4899;

  &:hover {
    background: #FFD93D;
    color: #2d3748;
  }
`

const AICaregiving = () => {
  const navigate = useNavigate()

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  const features = [
    {
      icon: '🏥',
      title: '24/7 Virtual Caregiver',
      description: 'Access personalized care assistance anytime, anywhere with an intelligent virtual companion that understands your unique health needs and provides continuous support.'
    },
    {
      icon: '🔍',
      title: 'AI Symptom Checker',
      description: 'Check symptoms instantly and learn about possible conditions with advanced AI analysis that provides reliable health insights and guidance on next steps.'
    },
    {
      icon: '💊',
      title: 'Medication Management',
      description: 'Get detailed information about medications, proper dosages, interactions, and side effects. Set reminders to never miss a dose and track your medication schedule.'
    },
    {
      icon: '📊',
      title: 'Health & Wellness Tracking',
      description: 'Monitor vital signs, track daily activities, log symptoms, and visualize your health trends over time with comprehensive wellness monitoring tools.'
    },
    {
      icon: '💝',
      title: 'Empathetic Support',
      description: 'Receive compassionate, personalized emotional support and encouragement throughout your health journey with AI that understands and responds to your feelings.'
    },
    {
      icon: '🔔',
      title: 'Care Routine Reminders',
      description: 'Stay on track with customizable reminders for medications, appointments, exercises, meals, and daily care activities to maintain your health routine.'
    },
    {
      icon: '📍',
      title: 'Nearby Hospital Locator',
      description: 'Quickly find nearby hospitals, clinics, pharmacies, and emergency care facilities with real-time directions and contact information when you need it most.'
    },
    {
      icon: '📱',
      title: 'Easy-to-Use Interface',
      description: 'Navigate a simple, intuitive interface designed for all ages and technical abilities, making healthcare accessible and stress-free for everyone.'
    }
  ]

  const benefits = [
    {
      icon: '🎯',
      title: 'Peace of Mind',
      description: 'Feel secure knowing you have 24/7 access to health guidance and support, helping you make informed decisions about your wellbeing.'
    },
    {
      icon: '👨‍👩‍👧',
      title: 'Family Connectivity',
      description: 'Keep loved ones informed about your health status and share important updates with family members and caregivers effortlessly.'
    },
    {
      icon: '⏰',
      title: 'Better Medication Adherence',
      description: 'Improve health outcomes with timely reminders and detailed medication information that helps you stick to your treatment plan.'
    },
    {
      icon: '📈',
      title: 'Proactive Health Management',
      description: 'Identify health concerns early with continuous monitoring and symptom tracking, enabling prompt intervention and better outcomes.'
    },
    {
      icon: '💰',
      title: 'Reduce Healthcare Costs',
      description: 'Minimize unnecessary doctor visits and emergency room trips by getting reliable health information and guidance at your fingertips.'
    },
    {
      icon: '🌟',
      title: 'Enhanced Independence',
      description: 'Maintain your independence and dignity while receiving the support you need to live confidently and healthily at home.'
    }
  ]

  const steps = [
    {
      number: '1',
      title: 'Create Your Profile',
      description: 'Set up your personalized health profile with medical history, current conditions, and medications.'
    },
    {
      number: '2',
      title: 'Connect with AI Caregiving Companion',
      description: 'Start conversations with your virtual caregiver and receive personalized health guidance and support.'
    },
    {
      number: '3',
      title: 'Track Your Health',
      description: 'Log symptoms, medications, and vital signs while receiving smart reminders and insights about your wellbeing.'
    },
    {
      number: '4',
      title: 'Access Care Resources',
      description: 'Find nearby hospitals, schedule appointments, and get emergency assistance whenever you need it.'
    }
  ]

  return (
    <Container>
      <HeroSection>
        <MaxWidthContainer>
          <HeroContent>
            <ProductLogo>AI Caregiving Companion</ProductLogo>
            <HeroTagline>Your Compassionate 24/7 Virtual Caregiver</HeroTagline>
            <HeroDescription>
              AI Caregiving Companion is an intelligent caregiving solution that provides round-the-clock health support, guidance,
              and empathy. From symptom checking and medication management to wellness tracking and emotional support,
              AI Caregiving Companion is always there when you need it most.
            </HeroDescription>
            <CTAButton onClick={() => navigate('/contact')}>
              Request a Demo
            </CTAButton>
          </HeroContent>
        </MaxWidthContainer>
      </HeroSection>

      <OverviewSection>
        <MaxWidthContainer>
          <SectionTitle>Your Personal Health Companion, Always by Your Side</SectionTitle>
          <SectionSubtitle>
            AI Caregiving Companion combines cutting-edge artificial intelligence with genuine empathy to deliver personalized
            healthcare support that understands your unique needs. Whether you're managing chronic conditions,
            recovering from illness, or simply maintaining wellness, AI Caregiving Companion is your trusted companion every step of the way.
          </SectionSubtitle>

          <FeaturesGrid>
            {features.map((feature, index) => (
              <FeatureCard key={index}>
                <FeatureIcon>{feature.icon}</FeatureIcon>
                <FeatureTitle>{feature.title}</FeatureTitle>
                <FeatureDescription>{feature.description}</FeatureDescription>
              </FeatureCard>
            ))}
          </FeaturesGrid>
        </MaxWidthContainer>
      </OverviewSection>

      <BenefitsSection>
        <MaxWidthContainer>
          <SectionTitle>Why Families Trust AI Caregiving Companion</SectionTitle>
          <SectionSubtitle>
            Thousands of users and their families rely on AI Caregiving Companion for compassionate, intelligent healthcare
            support that enhances quality of life and promotes independent living.
          </SectionSubtitle>

          <BenefitsList>
            {benefits.map((benefit, index) => (
              <BenefitItem key={index}>
                <BenefitIcon>{benefit.icon}</BenefitIcon>
                <BenefitContent>
                  <BenefitTitle>{benefit.title}</BenefitTitle>
                  <BenefitText>{benefit.description}</BenefitText>
                </BenefitContent>
              </BenefitItem>
            ))}
          </BenefitsList>
        </MaxWidthContainer>
      </BenefitsSection>

      <HowItWorksSection>
        <MaxWidthContainer>
          <SectionTitle>How AI Caregiving Companion Works</SectionTitle>
          <SectionSubtitle>
            Getting started with AI Caregiving Companion is simple. Set up your profile and start experiencing compassionate,
            intelligent healthcare support within minutes.
          </SectionSubtitle>

          <StepsGrid>
            {steps.map((step, index) => (
              <StepCard key={index}>
                <StepNumber>{step.number}</StepNumber>
                <StepTitle>{step.title}</StepTitle>
                <StepDescription>{step.description}</StepDescription>
              </StepCard>
            ))}
          </StepsGrid>
        </MaxWidthContainer>
      </HowItWorksSection>

      <CTASection>
        <MaxWidthContainer>
          <CTATitle>Experience the Future of Caregiving</CTATitle>
          <CTADescription>
            Join thousands who are living healthier, more independent lives with AI Caregiving Companion as their trusted
            virtual caregiver. Schedule a personalized demo and discover how AI Caregiving Companion can transform your healthcare experience.
          </CTADescription>
          <CTAButtonWhite onClick={() => navigate('/contact')}>
            Request a Demo
          </CTAButtonWhite>
        </MaxWidthContainer>
      </CTASection>
    </Container>
  )
}

export default AICaregiving
